import React, { memo, useCallback, useState, useEffect, useRef } from "react";
import {
  Grid as MuiGrid,
  Checkbox,
  FormLabel,
  Paper,
  FormControlLabel,
  Divider,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import type { Moment } from "moment";

import RpMuiSelectBase from "@/components/muiCustom/select/RpMuiSelect";
import { boostCampaignHelper } from "../utils/boostCampaignHelper";
import BoostHeaderTooltip from "./BoostHeaderTooltip";
import RpVirtualList from "@/components/virtual/RpVirtualList";
import RpDatePickerBase from "@/components/muiCustom/date/RpDatePicker";
import RpFormErrorBase from "@/components/muiCustom/error/RpFormError";
import RpCurrencyFieldBase from "@/components/muiCustom/textField/RpCurrencyField";
import { CHANNELS } from "@/config/constants";
import { getFormatDate } from "@/helpers/utils";
import { EstimatedImpressionsText } from "@/features/campaigns/shared/components/EstimatedImpressions";
import {
  getImpressionEstimation,
  getEstimatedImpressions,
} from "@/features/campaigns/shared/helpers/estimatedImpressionsHelper";

type Id = number | string;

type ChannelOption = {
  id: Id;
  label?: string;
  [key: string]: unknown;
};

type BoostLocationData = {
  fk_location_id: Id;
  location_name?: string;
  start_date?: string | Date | null;
  end_date?: string | Date | null;
  last_boosted_start_date?: string | Date | null;
  last_boosted_end_date?: string | Date | null;
  [key: string]: unknown;
};

type BoostSelectedLocation = {
  locationId: Id | "";
  start_date: string;
  end_date: string;
  boost_budget: string | number;
  current_budget: string | number;
  channels: Id[];
  [key: string]: unknown;
};

type BoostSettingValues = {
  selectedLoc?: Record<Id, BoostSelectedLocation>;
};

type BoostSettingErrors = {
  selectedLoc?: Record<
    Id,
    Partial<Record<keyof BoostSelectedLocation, string>>
  >;
  [key: string]: unknown;
};

type BoostLocationSettingProps = {
  locationData?: BoostLocationData[];
  channelsList?: ChannelOption[];
  is_selected_boost_type?: boolean;
  boosted_duration?: number;
  campaignHasSearchChannel?: boolean;
  campaignHasOnlyPMaxChannel?: boolean;
  campaignData?: Record<string, unknown>;
  LOC_INIT_STATE: BoostSelectedLocation;
};

type BoostBudgetFieldProps = {
  locationId: Id;
  formikValue?: string | number;
  isLocationSelected: boolean;
  errors?: BoostSettingErrors;
  setFieldValue: (_field: string, _value: unknown) => void;
  setFieldTouched: (_field: string, _isTouched?: boolean) => void;
};

const RpMuiSelect = RpMuiSelectBase as React.ComponentType<
  Record<string, unknown>
>;
const RpDatePicker = RpDatePickerBase as React.ComponentType<
  Record<string, unknown>
>;
const RpCurrencyField = RpCurrencyFieldBase as React.ComponentType<
  Record<string, unknown>
>;
const RpFormError = RpFormErrorBase as React.ComponentType<
  Record<string, unknown>
>;
const Grid = MuiGrid as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

const MobileLabel = ({ label }: { label: string }) => {
  return (
    <FormLabel className="d-lg-none fw-sb text-dark d-block">
      {label}
      <BoostHeaderTooltip label={label} />
    </FormLabel>
  );
};

// Separate component with local state to prevent cursor jumping
// This component maintains its own state during typing and syncs to Formik
const BoostBudgetField = memo(
  ({
    locationId,
    formikValue,
    isLocationSelected,
    errors,
    setFieldValue,
    setFieldTouched,
  }: BoostBudgetFieldProps) => {
    const [localValue, setLocalValue] = useState(() => formikValue || "");
    const isUserTypingRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sync from Formik only when not typing (external updates)
    useEffect(() => {
      if (!isUserTypingRef.current) {
        const newFormikValue = formikValue || "";
        if (newFormikValue !== localValue) {
          setLocalValue(newFormikValue);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formikValue]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    const handleChange = (e: { target: { value: string } }) => {
      const value = e.target.value;
      isUserTypingRef.current = true;
      setLocalValue(value);
      setFieldValue(`selectedLoc.${locationId}.boost_budget`, value);
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Reset typing flag after user stops typing
      timeoutRef.current = setTimeout(() => {
        isUserTypingRef.current = false;
      }, 300);
    };

    const handleBlur = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isUserTypingRef.current = false;
      // Ensure Formik has the latest value
      setFieldValue(`selectedLoc.${locationId}.boost_budget`, localValue);
      setFieldTouched(`selectedLoc.${locationId}.boost_budget`, true);
    };

    return (
      <RpCurrencyField
        name={`selectedLoc.${locationId}.boost_budget`}
        placeholder="$0.00 USD"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={!isLocationSelected}
        error={Boolean(
          isLocationSelected && errors?.selectedLoc?.[locationId]?.boost_budget
        )}
        helperText={
          <RpFormError
            error={errors?.selectedLoc?.[locationId]?.boost_budget}
            show={isLocationSelected}
            component="span"
          />
        }
        decimalScale={0}
      />
    );
  }
);

BoostBudgetField.displayName = "BoostBudgetField";

function BoostLocationSetting({
  locationData = [],
  channelsList = [],
  is_selected_boost_type,
  boosted_duration = 0,
  campaignHasSearchChannel,
  campaignHasOnlyPMaxChannel,
  campaignData,
  LOC_INIT_STATE,
}: BoostLocationSettingProps) {
  const { values, setFieldValue, setFieldTouched, errors } =
    useFormikContext<BoostSettingValues>();
  const formErrors = errors as BoostSettingErrors;

  // Update handlers to use location ID directly
  const handleOnSelectStartDate = (date: Moment | null, locationId: Id) => {
    setFieldValue(
      `selectedLoc.${locationId}.start_date`,
      date ? date.format("YYYY-MM-DD") : ""
    );
    setFieldValue(`selectedLoc.${locationId}.end_date`, "");
    setFieldValue(`selectedLoc.${locationId}.current_budget`, "");
  };

  const handleOnSelectEndDate = (date: Moment | null, locationId: Id) => {
    setFieldValue(
      `selectedLoc.${locationId}.end_date`,
      date ? date.format("YYYY-MM-DD") : ""
    );
  };

  const isSearchSelected = useCallback(
    (lc?: BoostSelectedLocation, checkInSelectionOnly = false) => {
      // Early return for non-selection check cases
      if (!checkInSelectionOnly) {
        if (!campaignHasSearchChannel) return false;
        if (!is_selected_boost_type) return campaignHasSearchChannel;
      }

      // Return false if no channels
      if (!lc?.channels?.length) return false;

      // Memoize channel IDs using useMemo
      const searchId = channelsList.find(
        (c) =>
          c?.id === CHANNELS.SEARCH.id ||
          c?.label?.toLowerCase() === CHANNELS.SEARCH.lowerCaseName
      )?.id;

      const pMaxId = channelsList.find(
        (c) =>
          c?.id === CHANNELS.PMAX.id ||
          c?.label?.toLowerCase() === CHANNELS.PMAX.lowerCaseName
      )?.id;

      // Check if either search or pMax channels are included
      return (
        lc.channels.includes(searchId || "") ||
        lc.channels.includes(pMaxId || "")
      );
    },
    [campaignHasSearchChannel, is_selected_boost_type, channelsList] // Removed unused dependency campaignHasOnlyPMaxChannel
  );

  return (
    <>
      <RpVirtualList
        items={locationData}
        renderItem={(loc, index) => {
          const locationId = loc.fk_location_id;
          const lc = values?.selectedLoc?.[locationId];

          const isLocationSelected = lc?.locationId === locationId;
          const isSearch = isSearchSelected(lc);
          const boostBudgetError =
            formErrors?.selectedLoc?.[locationId]?.boost_budget;
          const selectedChannelIds = new Set(
            (lc?.channels || []).map((channelId) => String(channelId))
          );
          const estimatedImpressionChannels = getImpressionEstimation(
            campaignData
          )?.canParticipantSelectSpecificChannels
            ? channelsList.filter((channel) =>
                selectedChannelIds.has(String(channel.id))
              )
            : channelsList;
          const estimatedImpressions = getEstimatedImpressions({
            budget: lc?.boost_budget,
            impressionEstimation: getImpressionEstimation(campaignData),
            channels: estimatedImpressionChannels,
            allocateBudgetAcrossBenchmarkedChannels: true,
          });

          const dateConstraints = boostCampaignHelper.getMinMaxDateConstraint({
            loc,
            boosted_duration,
            locFormData: lc,
            isSearch: isSearch,
          });

          return (
            <>
              <Paper
                className="p-2 pt-3 pb-3 pb-lg-1"
                key={locationId}
                elevation={0}
                square
              >
                <Grid container spacing={2} justifyContent="space-between">
                  <Grid item size={{ xs: 12, md: 2.4, lg: 2.4, sm: 12 }}>
                    <MobileLabel label="Location" />
                    <FormControlLabel
                      name={`selectedLoc.${locationId}.locationId`}
                      control={
                        <Checkbox
                          color="primary"
                          checked={isLocationSelected}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setFieldValue(`selectedLoc.${locationId}`, {
                              ...LOC_INIT_STATE,
                              locationId: isChecked ? locationId : "",
                              channels: isChecked
                                ? channelsList?.map((c) => c.id)
                                : [],
                            });
                          }}
                        />
                      }
                      label={
                        <div className="fw-sb">
                          <div>
                            {campaignHasOnlyPMaxChannel
                              ? "All Locations"
                              : loc?.location_name}
                          </div>
                          {loc?.last_boosted_start_date &&
                            loc?.last_boosted_end_date && (
                              <Typography
                                variant="caption"
                                className="fs-10 fw-sb"
                                component="div"
                              >
                                Last Adjusted:{" "}
                                <span className="text-nowrap">
                                  {`${getFormatDate(loc?.last_boosted_start_date, "MM/DD/YYYY")} - ${getFormatDate(loc?.last_boosted_end_date, "MM/DD/YYYY")}`}
                                </span>
                              </Typography>
                            )}
                        </div>
                      }
                    />
                  </Grid>

                  {!!is_selected_boost_type && (
                    <Grid item size={{ xs: 12, md: 2.4, lg: 2.4, sm: 12 }}>
                      <MobileLabel label="Type" />
                      <RpMuiSelect
                        options={channelsList?.map((c) => ({
                          label: c.label,
                          value: c.id,
                        }))}
                        name={`selectedLoc.${locationId}.channels`}
                        value={lc?.channels || []}
                        onChange={(selectedValues: Id[] = []) => {
                          setFieldValue(
                            `selectedLoc.${locationId}.channels`,
                            selectedValues || []
                          );

                          const wasSearch = isSearchSelected(lc, true);
                          const willBeSearch =
                            selectedValues.includes(CHANNELS.SEARCH.id) ||
                            selectedValues.includes(CHANNELS.PMAX.id);

                          // Reset dependent fields if search/pmax status changes
                          if (wasSearch !== willBeSearch) {
                            setFieldValue(
                              `selectedLoc.${locationId}.end_date`,
                              ""
                            );
                            setFieldValue(
                              `selectedLoc.${locationId}.current_budget`,
                              ""
                            );
                          }
                        }}
                        disabled={!isLocationSelected}
                        multiple
                        placeholder="Channels"
                      />
                      <RpFormError
                        error={formErrors?.selectedLoc?.[locationId]?.channels}
                        show={isLocationSelected}
                      />
                    </Grid>
                  )}

                  <Grid item size={{ xs: 12, md: 2.4, lg: 2.4, sm: 12 }}>
                    <MobileLabel label="Boost Start Date" />
                    <RpDatePicker
                      value={lc?.start_date || null}
                      onChange={(date) =>
                        handleOnSelectStartDate(date, locationId)
                      }
                      disabled={!isLocationSelected}
                      placeholder="Start Date"
                      minDate={dateConstraints.minStartDate}
                      maxDate={dateConstraints.maxStartDate}
                    />
                    <RpFormError
                      error={formErrors?.selectedLoc?.[locationId]?.start_date}
                      show={isLocationSelected}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 2.4, lg: 2.4, sm: 12 }}>
                    <MobileLabel label="Boost End Date" />
                    <RpDatePicker
                      value={lc?.end_date || null}
                      onChange={(date) =>
                        handleOnSelectEndDate(date, locationId)
                      }
                      disabled={!isLocationSelected || !lc?.start_date}
                      placeholder="End Date"
                      minDate={dateConstraints.minEndDate}
                      maxDate={
                        dateConstraints.maxEndDate[
                          dateConstraints.maxEndDate.length - 1
                        ]
                      }
                      shouldDisableDate={(date) => {
                        if (!isSearch) return false;
                        const validEndDates = dateConstraints.maxEndDate;
                        return !validEndDates.includes(
                          date.format("YYYY-MM-DD")
                        );
                      }}
                    />
                    <RpFormError
                      error={formErrors?.selectedLoc?.[locationId]?.end_date}
                      show={isLocationSelected}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12, md: 2.4, lg: 2.4, sm: 12 }}>
                    <MobileLabel label="Add Boost Budget" />
                    <BoostBudgetField
                      key={`boost_budget_${locationId}`}
                      locationId={locationId}
                      formikValue={
                        values?.selectedLoc?.[locationId]?.boost_budget
                      }
                      isLocationSelected={isLocationSelected}
                      errors={formErrors}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />
                    {isLocationSelected && !boostBudgetError ? (
                      <EstimatedImpressionsText
                        enabled={getImpressionEstimation(campaignData)?.enabled}
                        estimate={estimatedImpressions}
                      />
                    ) : null}
                  </Grid>
                </Grid>
              </Paper>
              <Divider
                className={index === locationData.length - 1 ? "d-none" : ""}
              />
            </>
          );
        }}
      />
    </>
  );
}

export default memo(BoostLocationSetting);
